<script setup>
import { formatDistanceToNow } from 'date-fns'
import { countries } from '~/utils'

const { $pb } = useNuxtApp()

const route = useRoute()

definePageMeta({
  title: 'Jobs in Category',
  layout: 'public'
})

const slug = route.params.slug

if (!slug) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Category not found'
  })
}

const { data, status, error } = await useAsyncData(`category-${slug}`, async () => {
  const category = await $pb.collection('categories').getFirstListItem(`slug = "${slug}"`)

  const jobs = await $pb.collection('jobs_view').getList(1, 100, {
    filter: `categories.id ?= "${category.id}"`,
    expand: 'startup,location,categories,tags',
    sort: '-publishAt',
    requestKey: null
  })

  return {
    category,
    jobs
  }
})

const category = computed(() => {
  if (!data?.value?.category) {
    return null
  }

  return data.value.category
})

// if (!category.value) {
//   throw createError({
//     statusCode: 404,
//     statusMessage: 'Category not found'
//   })
// }

const jobs = computed(() => {
  if (!data?.value?.jobs || data?.value?.jobs?.length === 0) {
    return null
  }

  return data.value.jobs.items
})

function fileUrl(record) {
  if (!record) {
    return null
  }

  return $pb.files.getURL(record, record.logo, {
    thumb: '80x0'
  })
}

function getCountryName(countryCode) {
  return countries.find(country => country.value === countryCode).name
}
</script>

<template>
  <div>
    <div v-if="status === 'pending'">
      <div class="flex flex-col justify-center items-center h-full">
        <UIcon name="i-heroicons-arrow-path" class="h-5 w-5 animate-spin" />
        <div class="mt-2 text-sm text-gray-500">
          Fetching jobs...
        </div>
      </div>
    </div>

    <UError v-if="error || !category"
      :error="{
        statusCode: 404,
        statusMessage: 'Category not found',
        message: 'We couldn\'t find the category you were looking for or something went wrong.'
      }"
    />

    <UContainer v-else-if="data" class="py-10">
      <div class="p-4 bg-(--ui-bg)">
        <h1 class="text-2xl font-bold">Opportunities in {{ category.name }}</h1>
      </div>

      <div v-if="!jobs || jobs?.length === 0">
        <div class="p-4 bg-(--ui-bg)">
          We don't currently have any jobs in this category.
        </div>
      </div>

      <div class="py-8 pb-20 grid grid-cols-1 gap-4">
        <div v-for="job in jobs" :key="job.id">
          <UCard>
            <div class="grid grid-cols-8 grow">
              <div class="col-span-8 md:col-span-2 flex flex-col items-start py-2 md:py-0">
                <div class="text-base font-medium">
                  <NuxtLink
                    :to="`/startups/${job?.expand?.startup?.slug}/${job.id}`"
                    class="hover:underline"
                  >
                    {{ job.title }}
                  </NuxtLink>
                </div>
                <div class="text-sm text-gray-500 dark:text-gray-400">
                  {{ job.shortDescription }}
                </div>
                <div class="pt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <UIcon name="i-heroicons-map-pin" class="w-5 h-5" />
                  {{ job.expand?.location?.city }}, {{ getCountryName(job.expand?.location?.country) }}
                </div>
              </div>

              <div class="col-span-8 md:col-span-2 flex flex-wrap items-center gap-1 py-2 md:py-0">
                <div
                  v-for="category in job.expand?.categories"
                  :key="category.id"
                >
                  <UBadge
                    :label="category.name"
                    color="neutral"
                    variant="subtle"
                  />
                </div>
              </div>

              <div class="col-span-8 md:col-span-2 flex flex-wrap items-center gap-1 py-2 md:py-0">
                <div
                  v-for="tag in job.expand?.tags"
                  :key="tag.id"
                >
                  <UBadge
                    :label="tag.name"
                    color="primary"
                    variant="subtle"
                  />
                </div>
              </div>


              <div class="col-span-4 md:col-span-1 flex justify-start items-center gap-1 text-xs text-gray-500 dark:text-gray-400 py-2 md:py-0">
                <UIcon name="i-heroicons-calendar-days" class="w-5 h-5" />
                Listed<br>
                {{ formatDistanceToNow(job.publishAt || new Date()) }} ago
              </div>

              <div class="col-span-4 md:col-span-1 flex items-center justify-end  py-2 md:py-0">
                <div>
                  <UButton
                    :to="`/startups/${data?.startup?.slug}/${job.id}`"
                    label="Apply Now"
                    color="success"
                    variant="solid"
                  />
                </div>
              </div>
            </div>
          </UCard>
        </div>
      </div>
    </UContainer>
  </div>
</template>
